/**
 * Metabase Filter Mapping Utilities
 *
 * Provides functions to convert Fixed Fee filters to Metabase-compatible parameters
 */

/**
 * Format date range for Metabase
 * Converts date objects from DateRangeSelect to Metabase-compatible format
 *
 * @param {Date|string|null} startDate - Start date from DateRangeSelect
 * @param {Date|string|null} endDate - End date from DateRangeSelect
 * @returns {string|null} Formatted date range for Metabase (YYYY-MM-DD~YYYY-MM-DD or null)
 */
export function formatDateRangeForMetabase(startDate, endDate) {
  // If no dates provided, return null (Metabase will use default)
  if (!startDate && !endDate) {
    return null;
  }

  // Convert to Date objects and use toNetworkDate() prototype method
  const start = startDate ? new Date(startDate).toNetworkDate() : '';
  const end = endDate ? new Date(endDate).toNetworkDate() : '';

  // Single date scenarios
  if (startDate && !endDate) {
    return `${start}~`;
  }
  if (!startDate && endDate) {
    return `~${end}`;
  }

  // Both dates provided - format as range
  return `${start}~${end}`;
}

/**
 * Format completion state for Metabase
 * @param {string} state - Completion state ('all', 'true', 'false')
 * @returns {string|null} Formatted completion state
 */
export function formatCompletionState(state) {
  if (!state || state === 'all') return null;
  return state; // 'true' or 'false'
}

/**
 * Format billed state for Metabase
 * Backend expects the state name string ('WIP', 'billed', 'draft')
 * @param {string} state - Billed state ('WIP', 'billed', 'draft', 'all')
 * @returns {string|null} State name or null for 'all'
 */
export function formatBilledState(state) {
  if (!state || state === 'all') return null; // No filter applied
  if (state === 'notFinal') {
    return 'Interim,WIP';
  }
  return state; // Return state name as-is ('WIP', 'billed', 'draft')
}

/**
 * Format charge type for Metabase (fixedfeetype parameter)
 * Backend maps this to DashboardFilter.FixedFeeType
 * @param {string} type - Charge type ('CompletionBased', 'DateBased', 'Recurring')
 * @returns {string|null} Fixed fee type string (CompletionBased, DateBased, Recurring)
 */
export function formatChargeType(type) {
  if (!type) return null;

  // Backend expects the exact enum values
  return type;
}

/**
 * Format client ID for Metabase
 * @param {string|null} clientId - Client UUID
 * @returns {string|null} Client ID or null
 */
export function formatClientId(clientId) {
  return clientId || null;
}

/**
 * Format matter ID for Metabase
 * @param {string|null} matterId - Matter UUID
 * @returns {string|null} Matter ID or null
 */
export function formatMatterId(matterId) {
  return matterId || null;
}